'use client';

import { useState } from 'react';
import Link from 'next/link';
import {
  Shield,
  MessageSquare,
  CheckCircle,
  ArrowRight,
  Star,
  MapPin,
  Scale,
  Building2,
  Calculator,
  AlertTriangle,
  TrendingUp,
  ClipboardList,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { HeroBriefingPreview } from '@/components/landing/hero-briefing-preview';
import { DemoBriefingModal } from '@/components/briefing/demo-briefing-modal';

const features = [
  {
    icon: Building2,
    title: 'Komplettes Unternehmensprofil',
    desc: 'Firmenstruktur, Gesellschafter, Geschäftsführung, Standorte, Finanzkennzahlen und Brancheneinordnung, alles recherchiert und aufbereitet, bevor Sie zum Hörer greifen.',
  },
  {
    icon: Calculator,
    title: 'Deckungssummen mit Begründung',
    desc: 'Konkrete Deckungssummen für 25+ Versicherungssparten, mit transparenten Berechnungswegen und nachvollziehbaren Begründungen, die Sie direkt im Gespräch nutzen können.',
  },
  {
    icon: AlertTriangle,
    title: '16-Punkte-Risikocheck',
    desc: 'Von Cyberangriffen über Produkthaftung bis Insolvenzhistorie: Wir durchsuchen aktuelle Quellen nach Vorfällen, Klagen und Risiken, inklusive NIS2-Prüfung und Compliance-Check.',
  },
  {
    icon: MessageSquare,
    title: 'Maßgeschneiderte Gesprächseinstiege',
    desc: 'Bis zu 7 fertige Gesprächseinstiege mit konkreten Zahlen und Szenarien aus dem Unternehmen, damit Sie vom ersten Satz an Kompetenz zeigen.',
  },
  {
    icon: TrendingUp,
    title: 'Cross-Selling auf einen Blick',
    desc: 'Priorisierte Verkaufschancen mit geschätztem Prämienvolumen pro Sparte, von bAV und bKV bis zur D&O. Sie sehen sofort, wo das größte Potenzial liegt.',
  },
  {
    icon: ClipboardList,
    title: 'Offene Fragen fürs Erstgespräch',
    desc: 'Strukturierte Checkliste mit den wichtigsten Fragen und identifizierten Datenlücken, damit im Gespräch nichts Wichtiges untergeht.',
  },
];

const steps = [
  {
    num: '1',
    title: 'Firmenname eingeben',
    desc: 'Einfaches Eingabefeld \u2013 kein kompliziertes Setup nötig',
  },
  {
    num: '2',
    title: 'salesbriefing.de recherchiert',
    desc: 'Automatische Analyse aus mehreren hundert Quellen in Echtzeit',
  },
  {
    num: '3',
    title: 'Briefing erhalten',
    desc: 'Strukturierter Report mit allem was Sie brauchen. In unter 15 Minuten.',
  },
];

const testimonials = [
  {
    name: 'Stefan R.',
    role: 'Versicherungsmakler, Stuttgart',
    quote:
      'Ich hab das Briefing mit meiner eigenen Recherche verglichen. Nach 4 Stunden hatte ich vielleicht ein Drittel der Infos und die waren weniger strukturiert. Das hier ist auf einem komplett anderen Level.',
  },
  {
    name: 'Elif Ö.',
    role: 'Versicherungsmaklerin, Düsseldorf',
    quote:
      'Mein Kunde hat mich gefragt, woher ich so viel über sein Unternehmen weiß. Das war nach dem ersten Briefing. Seitdem nutze ich es vor jedem Termin.',
  },
  {
    name: 'Jürgen W.',
    role: 'Versicherungsmakler, Frankfurt',
    quote:
      'salesbriefing.de hat einen Ransomware-Angriff auf einen Zulieferer meines Kunden gefunden, den nicht mal der Kunde selbst auf dem Schirm hatte. Daraus wurde eine Cyber-Versicherung mit OT-Deckung, die jetzt sein gesamtes Produktionsnetz absichert. Ohne das Briefing hätte ich das Thema nie angesprochen.',
  },
];

export function HomeClient({ isAuthenticated }: { isAuthenticated: boolean }) {
  const [demoOpen, setDemoOpen] = useState(false);
  const ctaHref = isAuthenticated ? '/dashboard' : '/register';

  return (
    <div>
      {/* Hero */}
      <section className="relative overflow-hidden bg-teal-50 dark:bg-card">
        <div className="container mx-auto px-4 py-16 md:py-24">
          <div className="grid items-center gap-10 lg:grid-cols-2">
            <div className="max-w-xl">
              <h1 className="text-3xl font-extrabold tracking-tight text-foreground leading-tight md:text-5xl">
                Wissen, was Ihr Kunde braucht,{' '}
                <span className="text-gradient">bevor die Konkurrenz es weiß.</span>
              </h1>
              <p className="mt-4 text-lg text-muted-foreground leading-relaxed">
                Sie geben den Firmennamen ein, salesbriefing.de liefert Ihnen alles Weitere:
                Unternehmensstruktur, Leitungspersonen, relevante Versicherungssparten mit
                konkreten Deckungssummen und nachvollziehbaren Begründungen. Damit Sie im
                Gespräch von der ersten Sekunde an überzeugen.
              </p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link href={ctaHref}>
                  <Button variant="hero" size="lg" className="min-h-[44px] min-w-[44px]">
                    {isAuthenticated ? 'Zum Dashboard' : 'Kostenlos testen'}
                    <ArrowRight className="ml-1 h-4 w-4" />
                  </Button>
                </Link>
                <Button
                  variant="hero-outline"
                  size="lg"
                  className="min-h-[44px] min-w-[44px]"
                  onClick={() => setDemoOpen(true)}
                >
                  Beispiel-Briefing ansehen
                </Button>
              </div>
            </div>
            <div className="relative cursor-pointer" onClick={() => setDemoOpen(true)}>
              <HeroBriefingPreview />
              <div className="absolute inset-0 rounded-xl opacity-0 hover:opacity-100 transition-opacity bg-black/5 dark:bg-white/5 flex items-center justify-center">
                <span className="bg-primary text-primary-foreground px-4 py-2 rounded-lg text-sm font-semibold shadow-lg">
                  Komplettes Beispiel-Briefing öffnen
                </span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section id="features" className="bg-teal-50 dark:bg-background py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="text-2xl font-bold text-foreground md:text-3xl">
              Ein Briefing. Alles drin.
            </h2>
            <p className="mt-3 text-muted-foreground">
              Was Sie sonst in Stunden manuell zusammensuchen, liefert salesbriefing.de in einem Report.
            </p>
          </div>
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {features.map((f) => (
              <Card
                key={f.title}
                className="card-shadow hover:card-shadow-hover transition-shadow border-border"
              >
                <CardContent className="p-6">
                  <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-lg bg-accent/10 text-accent">
                    <f.icon className="h-5 w-5" />
                  </div>
                  <h3 className="font-semibold text-foreground">{f.title}</h3>
                  <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{f.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="bg-teal-50 dark:bg-card py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="text-2xl font-bold text-foreground md:text-3xl">
              So funktioniert&apos;s
            </h2>
            <p className="mt-3 text-muted-foreground">
              In drei einfachen Schritten zum fertigen Briefing.
            </p>
          </div>
          <div className="grid gap-8 md:grid-cols-3">
            {steps.map((s) => (
              <div key={s.num} className="text-center">
                <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-accent text-accent-foreground text-lg font-bold shadow-md">
                  {s.num}
                </div>
                <h3 className="text-lg font-semibold text-foreground">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Social proof */}
      <section className="bg-teal-50 dark:bg-background py-16 md:py-24">
        <div className="container mx-auto px-4">
          <div className="mb-12 text-center">
            <h2 className="text-2xl font-bold text-foreground md:text-3xl">
              Entwickelt für Versicherungsmakler
            </h2>
            <p className="mt-3 text-muted-foreground">
              Für alle, die mehr über das Unternehmen wissen wollen als der Kunde selbst.
            </p>
          </div>
          <div className="grid gap-6 md:grid-cols-3">
            {testimonials.map((t) => (
              <Card key={t.name} className="card-shadow border-border">
                <CardContent className="p-6">
                  <div className="mb-3 flex gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="h-4 w-4 fill-amber-400 text-amber-400" />
                    ))}
                  </div>
                  <p className="text-sm italic text-muted-foreground leading-relaxed">
                    &bdquo;{t.quote}&ldquo;
                  </p>
                  <div className="mt-4 flex items-center gap-3">
                    <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary/10 text-primary text-sm font-semibold">
                      {t.name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')}
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground">{t.name}</p>
                      <p className="text-xs text-muted-foreground">{t.role}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* Trust badges */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-6">
            <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground card-shadow">
              <Shield className="h-4 w-4 text-accent" /> DSGVO-konform
            </div>
            <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground card-shadow">
              <MapPin className="h-4 w-4 text-accent" /> Hosting in Deutschland
            </div>
            <div className="flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm text-muted-foreground card-shadow">
              <Scale className="h-4 w-4 text-accent" /> Keine Beratung i.S.d. §34d GewO
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-gradient-to-br from-teal-700 via-teal-800 to-teal-900 py-20 md:py-24">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl font-bold text-white md:text-5xl tracking-tight leading-tight">
            Wissen.
            <br />
            Was die Konkurrenz nicht weiß.
          </h2>
          <p className="mx-auto mt-5 max-w-xl text-lg text-white/80 leading-relaxed">
            Generieren Sie Ihr erstes Briefing kostenlos und sehen Sie selbst, warum
            Versicherungsmakler in der DACH-Region auf salesbriefing.de setzen.
          </p>
          <Link href={ctaHref} className="mt-8 inline-block">
            <Button size="lg" className="text-base font-semibold shadow-lg min-h-[44px] bg-white text-teal-800 hover:bg-white/90">
              {isAuthenticated ? 'Zum Dashboard' : 'Jetzt kostenlos testen'}
              <ArrowRight className="ml-1 h-4 w-4" />
            </Button>
          </Link>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-white/70">
            <span className="flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4" /> Test ohne Kreditkarte
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4" /> DSGVO-konform
            </span>
            <span className="flex items-center gap-1.5">
              <CheckCircle className="h-4 w-4" /> Monatlich kündbar
            </span>
          </div>
          <p className="mt-10 text-sm text-white/70">
            Sie suchen die M&amp;A-Variante?{' '}
            <Link href="/m-a" className="font-semibold text-white underline">
              Zur M&amp;A-Recherche
            </Link>
          </p>
        </div>
      </section>

      {/* Demo briefing modal (PROJ-29) */}
      <DemoBriefingModal open={demoOpen} onOpenChange={setDemoOpen} />
    </div>
  );
}
